import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarDays, Euro, LogOut, MessageSquare, RefreshCw,
  Settings as SettingsIcon, Star, LayoutList, Loader2, Activity, Sparkles,
} from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PricesTab from "@/pages/admin/PricesTab";
import SeasonsTab from "@/pages/admin/SeasonsTab";
import BookingsTab from "@/pages/admin/BookingsTab";
import TaxTab from "@/pages/admin/TaxTab";
import AudienceTab from "@/pages/admin/AudienceTab";
import ReviewsTab from "@/pages/admin/ReviewsTab";
import MessagesTab from "@/pages/admin/MessagesTab";
import SettingsTab from "@/pages/admin/SettingsTab";

const TABS = [
  { id: "prices", label: "Calendrier & prix", icon: CalendarDays },
  { id: "seasons", label: "Saisons & vacances", icon: Sparkles },
  { id: "bookings", label: "Réservations", icon: LayoutList },
  { id: "tax", label: "Taxe de séjour", icon: Euro },
  { id: "audience", label: "Audience", icon: Activity },
  { id: "reviews", label: "Avis", icon: Star },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "settings", label: "Réglages & Airbnb", icon: SettingsIcon },
];

const StatCards = ({ stats }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="admin-stats">
    {[
      { l: "Séjours totaux réalisés", v: stats?.stays_done ?? "—", i: LayoutList, t: "stays-done" },
      { l: "Séjours totaux à venir", v: stats?.stays_upcoming ?? "—", i: CalendarDays, t: "stays-upcoming" },
      { l: "Séjours ce mois-ci", v: stats?.stays_month ?? "—", i: CalendarDays, t: "stays-month" },
      { l: "Montant total encaissé", v: stats ? eur(stats.revenue_captured) : "—", i: Euro, t: "revenue-captured" },
      { l: "Revenu sur le mois", v: stats ? eur(stats.revenue_month) : "—", i: Euro, t: "revenue-month" },
    ].map((s) => (
      <div key={s.l} className="border border-white/10 bg-panel p-5" data-testid={`stat-${s.t}`}>
        <s.i size={16} className="text-brass" />
        <p className="mt-3 font-display text-2xl text-bone">{s.v}</p>
        <p className="text-xs text-muted-foreground">{s.l}</p>
      </div>
    ))}
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("prices");
  const [gites, setGites] = useState([]);
  const [stats, setStats] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [version, setVersion] = useState(0);

  const loadStats = useCallback(() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  const refreshAll = useCallback(() => { loadStats(); setVersion((v) => v + 1); }, [loadStats]);
  useEffect(() => {
    api.get("/gites").then((r) => setGites(r.data)).catch(() => {});
    loadStats();
  }, [loadStats]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-ical");
      const ok = data.results.filter((r) => r.synced);
      ok.length ? toast.success(`Synchronisé : ${ok.map((r) => `${r.gite_slug} (${r.blocked_days} nuits)`).join(", ")}`)
                : toast.error(data.results[0]?.reason || "Aucune synchronisation");
      loadStats();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSyncing(false); }
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-page">
      <header className="border-b border-white/10 bg-panel">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-5 py-4 sm:px-8">
          <div>
            <p className="font-display text-xl text-bone">Tableau de bord</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button onClick={sync} disabled={syncing} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="header-sync-btn">
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync Airbnb
            </button>
            <Link to="/" className="btn-ghost !py-2 !px-4 !text-xs">Voir le site</Link>
            <button onClick={logout} className="btn-primary !py-2 !px-4 !text-xs" data-testid="logout-btn"><LogOut size={14} /> Quitter</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <StatCards stats={stats} />

        {stats?.sync?.length ? (
          <p className="mt-4 text-xs text-muted-foreground" data-testid="sync-state">
            Dernière synchro iCal (Airbnb + Booking.com) : {stats.sync
              .filter((s) => gites.some((g) => g.slug === s.gite_slug && g.active))
              .map((s) => `${gites.find((g) => g.slug === s.gite_slug)?.name || s.gite_slug} — ${s.last_sync ? new Date(s.last_sync).toLocaleString("fr-FR") : "jamais"} (${s.count} nuits bloquées)`)
              .join(" · ") || "aucun gîte ouvert à la réservation"}
          </p>
        ) : null}

        <nav className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-3">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${tab === t.id ? "bg-brass text-ink" : "text-bone hover:bg-white/[0.06]"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </nav>

        <div className="mt-8">
          {tab === "prices" && <PricesTab key={version} gites={gites} />}
          {tab === "seasons" && <SeasonsTab gites={gites} />}
          {tab === "bookings" && <BookingsTab gites={gites} onChanged={refreshAll} />}
          {tab === "tax" && <TaxTab key={version} />}
          {tab === "audience" && <AudienceTab />}
          {tab === "reviews" && <ReviewsTab />}
          {tab === "messages" && <MessagesTab onChanged={loadStats} />}
          {tab === "settings" && <SettingsTab gites={gites} onSync={sync} />}
        </div>
      </div>
    </div>
  );
}
